import { useApolloClient } from '@apollo/client';
import { Box } from '@mui/system';
import { appRoutes } from 'app/app-routes';
import ConfirmDialog, { ConfirmDialogProps } from 'common/components/ConfirmDialog';
import { ListingStatus } from 'common/types/listing-status';
import { LISTINGS_SEARCH_QUERY } from 'data/ListingsSearchQuery.graphql';
import {
  Listing,
  ListingAdd_Input,
  ListingsSearchQuery,
  ListingUpdate_Input,
  useListingsDeleteMutation,
  useListingsUpdateMutation,
} from 'graphql/generated';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { cacheActions } from 'redux/cache.slice';
import { newListingSelectors } from 'redux/new-listing.slice';
import { RootState } from 'redux/root-reducer';
import WsButton from 'stories/inputs/WsButton';
import WsFlexbox from 'stories/layout/WsFlexbox';
import WsGridContainer from 'stories/layout/WsGridContainer';
import WsInfoFrame from 'stories/layout/WsInfoFrame';

import ListingSummary from './ListingSummary';

type RouteParams = 'id';

const ListingInfo = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const client = useApolloClient();
  const { id } = useParams<Record<RouteParams, string>>();

  const [deleteListing] = useListingsDeleteMutation();
  const [updateListing] = useListingsUpdateMutation();

  const [listing, listingTo] = React.useState<Listing | ListingAdd_Input | undefined>(undefined);
  const [showDeleteDialog, showDeleteDialogTo] = React.useState(false);
  const [showSetActiveDialog, showSetActiveDialogTo] = React.useState(false);
  const [showSetInactiveDialog, showSetInactiveDialogTo] = React.useState(false);

  const cacheListing = useSelector((state: RootState) => newListingSelectors.listing(state));

  useEffect(() => {
    if (!id) navigate(appRoutes.errorPage);
    else {
      const findListing = async (id: string) => {
        if (id === 'cache') {
          console.log('loading cache');
          listingTo(cacheListing);
        } else {
          const { data } = await client.query<ListingsSearchQuery>({
            query: LISTINGS_SEARCH_QUERY,
            variables: { filters: [{ field: 'id', strVal: id }] },
          });
          if (data?.listingsSearch) {
            const listing = data.listingsSearch.listings[0];
            console.log('listing find', data.listingsSearch.listings[0]);
            listingTo(listing);
          }
        }
      };
      findListing(id);
    }
  }, [id, navigate, client, cacheListing]);

  const onConfirmDelete = async () => {
    try {
      showDeleteDialogTo(false);

      await deleteListing({
        variables: { listingsDeleteId: id! },
      });
      await client.clearStore();
      navigate(appRoutes.sell);
      dispatch(
        cacheActions.addSnack({
          message: 'Listing Deleted',
          variant: 'success',
        }),
      );
    } catch (error: any) {
      dispatch(
        cacheActions.addSnack({
          message: error.message,
          variant: 'error',
        }),
      );
    }
  };

  const onConfirmSetInactive = async () => {
    console.log('set inactive');
    showSetInactiveDialogTo(false);
    try {
      const li: ListingUpdate_Input = {
        id: id!,
        status: ListingStatus.inactive.toString(),
      };
      await updateListing({
        variables: {
          object: li,
        },
      });
      await client.clearStore();
      dispatch(
        cacheActions.addSnack({
          message: 'Listing Set to Inactive',
          variant: 'success',
        }),
      );
    } catch (error: any) {
      dispatch(
        cacheActions.addSnack({
          message: error.message,
          variant: 'error',
        }),
      );
    }
  };

  const onConfirmSetActive = async () => {
    console.log('set active');
    showSetActiveDialogTo(false);
    try {
      await updateListing({
        variables: {
          object: {
            id: id!,
            status: ListingStatus.active.toString(),
          },
        },
      });
      await client.clearStore();
      dispatch(
        cacheActions.addSnack({
          message: 'Listing Set to Active',
          variant: 'success',
        }),
      );
    } catch (error: any) {
      dispatch(
        cacheActions.addSnack({
          message: error.message,
          variant: 'error',
        }),
      );
    }
  };

  const onConfirmCancel = () => {
    showDeleteDialogTo(false);
    showSetActiveDialogTo(false);
    showSetInactiveDialogTo(false);
  };

  const onDeleteIntent = () => {
    showDeleteDialogTo(true);
  };

  const onSetActiveIntent = () => {
    showSetActiveDialogTo(true);
  };

  const onSetInactiveIntent = () => {
    showSetInactiveDialogTo(true);
  };

  const deleteDialogProps: ConfirmDialogProps = {
    open: showDeleteDialog,
    title: 'Delete Listing',
    content: 'Are you sure you want to delete this listing?',
    ackLabel: 'Delete',
    ackAction: onConfirmDelete,
    nackLabel: 'Cancel',
    nackAction: onConfirmCancel,
  };

  const setInactiveDialogProps: ConfirmDialogProps = {
    open: showSetInactiveDialog,
    title: 'Set Listing Inactive',
    content: 'Are you sure you want to set this listing to inactive?',
    ackLabel: 'Set Inactive',
    ackAction: onConfirmSetInactive,
    nackLabel: 'Cancel',
    nackAction: onConfirmCancel,
  };

  const setActiveDialogProps: ConfirmDialogProps = {
    open: showSetActiveDialog,
    title: 'Set Listing to Active',
    content: 'Are you sure you want to set this listing to active?',
    ackLabel: 'Set to Active',
    ackAction: onConfirmSetActive,
    nackLabel: 'Cancel',
    nackAction: onConfirmCancel,
  };

  return (
    <WsGridContainer>
      <ConfirmDialog {...deleteDialogProps} />
      <ConfirmDialog {...setInactiveDialogProps} />
      <ConfirmDialog {...setActiveDialogProps} />
      <WsInfoFrame width="80%" title="Listing Info">
        {listing && (
          <>
            <WsFlexbox gap={1} sx={{ paddingLeft: 0 }}>
              <Link to={appRoutes.sell}>
                <WsButton label="Back to Listings" variant="text" />
              </Link>
              <Box sx={{ flexGrow: 1 }} />
              {/* <WsButton label="Edit" size="small" variant="outlined" />
              {listing.status === ListingStatus.active && (
                <WsButton label="Set Inactive" size="small" variant="outlined" onClick={() => onSetInactiveIntent()} />
              )}
              {listing.status === ListingStatus.inactive && (
                <WsButton label="Set Active" size="small" variant="outlined" onClick={() => onSetActiveIntent()} />
              )}
              <WsButton label="Delete" size="small" variant="outlined" onClick={() => onDeleteIntent()} /> */}
            </WsFlexbox>
            <ListingSummary
              {...{
                year: listing.year,
                make: listing.make,
                model: listing.model,
                trim: listing.trim,
                trimId: listing.trimId,
                heading: listing.heading,
                mileage: listing.mileage,
                price: listing.price,
                city: listing.city,
                state: listing.state,
                zipCode: listing.zipCode,
                email: listing.email,
                phone: listing.phone,
                conditionType: listing.conditionType,
                titleStatus: listing.titleStatus,
                bodyType: listing.bodyType,
                bodyTypeOther: listing.bodyTypeOther,
                colorType: listing.colorType,
                colorTypeOther: listing.colorTypeOther,
                cylinderType: listing.cylinderType,
                cylinderTypeOther: listing.cylinderTypeOther,
                doorType: listing.doorType,
                doorTypeOther: listing.doorTypeOther,
                drivetrainType: listing.drivetrainType,
                drivetrainTypeOther: listing.drivetrainTypeOther,
                fuelType: listing.fuelType,
                fuelTypeOther: listing.fuelTypeOther,
                transmissionType: listing.transmissionType,
                transmissionTypeOther: listing.transmissionTypeOther,
                images: listing.images,
                description: listing.description,
                listingDate: listing.listingDate,
                status: listing.status,
                statusDate: listing.statusDate,
                userId: listing.userId,
                showContactInfo: listing.showContactInfo,
              }}
            />
          </>
        )}
      </WsInfoFrame>
    </WsGridContainer>
  );
};

export default ListingInfo;
